import { Map as TLMDMap } from './map';
import { PixelProjection, GeodeticBounds, Geodetic, Pixel } from './map-types';
import { Size } from '../data/common';
import { Subscription } from 'rxjs';
import { delegatedObserver, markup2element } from '../dom/dom';
import { Datum, DataMap, AnimationMap, MapOverlayDatumReader, SpatialCoordinate, SpatialDataCoordinate } from '../data-source/types';
import { VisualId, createElemSelector } from '../data-source/visualizer';
import { CursorMillis } from '../timeline/timeline-common';

const DEBUG = false;


export enum MapOverlayCategory {
	BOTTOM = "bottom",
	MODELLING_RESULT = "modelling_result",
	MODELLING_INPUT = "modelling_input",
	SENSOR = "sensor",
	OBSERVATION = "observation",
	OBSERVATION_AOI = "observation_aoi",
	OBSERVATION_TRAJECT = "observation_traject",
	TOP = "top"
}

const mapOverlayZOrder: string[] = [
	MapOverlayCategory.BOTTOM,
	MapOverlayCategory.MODELLING_RESULT,
	MapOverlayCategory.MODELLING_INPUT,
	MapOverlayCategory.SENSOR,
	MapOverlayCategory.OBSERVATION_TRAJECT,
	MapOverlayCategory.OBSERVATION,
	MapOverlayCategory.OBSERVATION_AOI, // ¯\_(ツ)_/¯  move to wherever more appropriate
	MapOverlayCategory.TOP
]

const getZOrder = (category: string | MapOverlayCategory): number => {
	let zOrder = mapOverlayZOrder.indexOf(category);
	return zOrder >= 0 ? zOrder : 0;
}
/**
 * The base map overlay
 */

export abstract class OverlayView {


	private __id: string;
	private _map: TLMDMap;
	private _layer: HTMLElement;

	private overlayViewInstance: google.maps.OverlayView;
	private subscriptions: Subscription[] = [];
	private categoryZOrder: number;


	public constructor(private category: MapOverlayCategory, private elementData: { [key: string]: string }, private enableEvents = true) {
		this.__id = '' + new Date().getTime() + '_' + Math.floor(Math.random() * 1001);
		this._map = undefined;
		this._layer = undefined;

		this.overlayViewInstance = new google.maps.OverlayView();

		this.overlayViewInstance.onAdd = () => this.onAdd();
		this.overlayViewInstance.onRemove = () => this.onRemove();
		this.overlayViewInstance.draw = () => this.update();

		this.categoryZOrder = getZOrder(category);
	}

	public setMap(map: TLMDMap): void {
		if (map) {
			if (this._map) {
				//we already have a map
				if (this._map.getId() !== map.getId()) {
					//we already have another  map
					this.overlayViewInstance.setMap(undefined);
					this._map = map;
					this.overlayViewInstance.setMap(this._map.getGMap());
				} else {
					// the given map is already set
				}
			} else {
				//we did not have a map before
				this._map = map;
				this.overlayViewInstance.setMap(this._map.getGMap());
			}
		} else {
			if (this._map) {
				//we have a map that needs to be removed
				this._map = undefined;
				this.overlayViewInstance.setMap(undefined);
			}
		}
	}

	public getId(): string {
		return this.__id
	}

	public getMap(): TLMDMap {
		return this._map;
	}

	private getPane(): Element {
		if (this._map != null) {
			return this.enableEvents ? this.overlayViewInstance.getPanes().overlayMouseTarget : this.overlayViewInstance.getPanes().overlayLayer;
		} else {
			return undefined;
		}
	}
	protected getLayer(): HTMLElement {
		return this._layer;
	}

	public getProjection(): PixelProjection {
		return this._map ? this._map.getLocalProjection() : undefined;
	};


	private onAdd(): void {
		this.unsubscribe();//to be sure but should(is) not be necesarry

		this._layer = document.createElement('div');

		if (DEBUG) {
			this._layer.style.border = '1px solid #FF0000';
		} else {
			this._layer.style.borderStyle = 'none';
			this._layer.style.borderWidth = '0px';
		}
		this._layer.style.position = 'absolute';

		if (this.elementData) {
			Object.entries(this.elementData).forEach(entry => this._layer.dataset[entry[0]] = entry[1]);
		}

		this._layer.dataset.categoryzorder = this.category;

		let pane = <HTMLElement>this.getPane();
		let layers = pane.children;
		let i = 0;
		while (i < layers.length && getZOrder((<HTMLElement>layers[i]).dataset.categoryzorder) <= this.categoryZOrder) {
			i++;
		}
		pane.insertBefore(this._layer, pane.children.item(i));

		this.subscribe();
	}

	private onRemove(): void {
		this.unsubscribe();

		this._layer.parentNode.removeChild(this._layer);
		this._layer = undefined;
	}

	protected draw(): void {
		if (this._layer) {
			let position = this.overlayViewInstance.getProjection().fromLatLngToDivPixel(new google.maps.LatLng(this.getMap().getAnchor()));

			this._layer.style.left = position.x + 'px';
			this._layer.style.top = position.y + 'px';
			this._layer.style.width = '0px';
			this._layer.style.height = '0px';
		}
	};

	protected update(): void {
		if (this._layer) {
			let self = this;
			window.requestAnimationFrame(() => self.draw());
		}
	}


	/**
	* override to add event subscriptions
	 */
	protected subscribe(): void {
		//		this.addSubscribtion(
		//			delegatedObserver(
		//				this.getLayer(),
		//				'click',
		//				'svg'
		//			)
		//				.subscribe(ev => { console.log('svg: %o', ev); }));

	};

	protected addSubscribtion(subscription: Subscription): void {
		this.subscriptions.push(subscription);
	};

	private unsubscribe(): void {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions.length = 0;
	};
}

export interface MapItemBounds extends SpatialCoordinate {
	type: MapItemType,
	northPX: number,
	eastPX: number,
	southPX: number,
	westPX: number,
	widthPX: number
	heightPX: number
	latPX?: number,
	lngPX?: number,
}

export interface MapDataCluster extends MapItemBounds {
	dataItemIds: string[],
	bounds: MapItemBounds[],
	dataItems: { [key: string]: Datum },
	count: number
}

interface MapItemMetaData extends MapDataCluster {
	elemId: string,
	selected: boolean
}
export enum MapLayout {
	overlap = 'overlap',
	combine = 'combine'
}

export enum MapItemType {
	location = 1,
	area = 2,
	combined = 4
}

export interface MapOverlayContentProducer {
	draw: (container: HTMLElement, className: string, localProjection: PixelProjection) => VisualId[],
	getBounds: () => GeodeticBounds
}
export interface MapOverlayDataContentProducer extends MapOverlayContentProducer {
	setData: (data: DataMap) => void,
	setAnimationModes: (modes: AnimationMap) => void,
	setAnimationPosition: (animationPosition: CursorMillis) => void,
	getItem: (elemId: string) => { group: string, item: MapItemMetaData },
	findItem: (datumId: string) => { group: string, item: MapItemMetaData },
	highlight: (container: HTMLElement) => void
}

export interface MapRenderer {
	size: () => Size,
	draw: (domElem: HTMLElement, meta: any, minimumSize: Size, localProjection: (location: Geodetic) => Pixel) => void,
	overlap: (item0: MapItemBounds, item1: MapItemBounds) => boolean
	merge: (clusters: MapDataCluster[], minimumSize: Size) => MapDataCluster
}

export interface MapOverlayOptions {
	elementData?: { [key: string]: string },
	eventHandlers?: { [key: string]: (evAndTrgt: { event: Event, target: HTMLElement }, data?: any) => void }
}

export interface MapOverlayContentProducerOptions {
	layout?: MapLayout
}
export interface MapLayoutResult {
	mpItems: Map<string, MapItemMetaData[]>,
	dataItem2mpItemMap: Map<string, number[]>,
	mpItemList: VisualId[]
}

/**
 * The base map overlay
 */
export class MapOverlay extends OverlayView {

	public constructor(category: MapOverlayCategory, private className: string, private options?: MapOverlayOptions, private contentProducer?: MapOverlayContentProducer) {
		super(category, options ? options.elementData : undefined);
		if (!this.options) {
			this.options = {}
		}
	}

	public setProducer(contentProducer: MapOverlayContentProducer): MapOverlay {
		this.contentProducer = contentProducer;
		this.update();
		return this;
	}

	public getProducer(): MapOverlayContentProducer {
		return this.contentProducer;
	}

	protected draw() {
		super.draw();
		let drawResult = undefined;
		if (this.getLayer()) {
			if (this.contentProducer) {
				drawResult = this.contentProducer.draw(
					this.getLayer(),
					this.className,
					this.getProjection());
			} else {
				this.getLayer().innerHTML = '';
			}
		}

		return drawResult;
	}

	public inView(): MapOverlay {
		if (this.getMap() && this.contentProducer) {
			this.getMap().getGMap().fitBounds(new google.maps.LatLngBounds(this.contentProducer.getBounds().southWest, this.contentProducer.getBounds().northEast));
		}
		return this;
	}

	public getSpatialCoordinate(): SpatialDataCoordinate {
		let bounds = this.contentProducer ? this.contentProducer.getBounds() : undefined;
		if (this.getMap() && bounds) {
			return {
				north: bounds.northEast.lat,
				east: bounds.northEast.lng,
				south: bounds.southWest.lat,
				west: bounds.southWest.lng,
				lat: ((bounds.northEast.lat + bounds.southWest.lat) / 2),//this will not work on date boundary
				lng: ((bounds.northEast.lng + bounds.southWest.lng) / 2),
			};
		}
		return undefined;
	}

	protected subscribe(): void {
		super.subscribe();
		if (this.options.eventHandlers) {
			Object.entries(this.options.eventHandlers).forEach(eventDefinition => {
				this.addSubscribtion(
					delegatedObserver(
						this.getLayer(),
						eventDefinition[0],
						`div.${this.className}`)
						.subscribe(eventDefinition[1]));
			});
		}
	}

}

export class MapOverlayDatumContentProducer implements MapOverlayDataContentProducer {

	private mapItems: Map<string, MapItemMetaData[]> = undefined;
	private dataItem2mapItemMap: Map<string, number[]> = undefined;

	private options: MapOverlayContentProducerOptions;
	private getData: () => DataMap = () => { return {}; };
	protected getAnimationModes: () => AnimationMap = () => { return {}; };
	private getAnimationPosition: () => CursorMillis = () => { return undefined; };

	public constructor(private renderer: MapRenderer,
					   private datumReader: MapOverlayDatumReader,
					   options: MapOverlayContentProducerOptions,
					   // dom id prefix for items within overlay (prefix+_+someId)
					   protected mapPrefix: string = 'mp') {
		this.setOptions(options);
	}

	public setOptions(options: MapOverlayContentProducerOptions): VisualId[] {

		this.options = Object.assign({ layout: MapLayout.overlap }, options);

		return undefined;
	}

	public setAnimationPosition(animationPosition: CursorMillis): void {
		this.getAnimationPosition = () => animationPosition;
	}

	public setData(data: DataMap): void {
		this.getData = () => data;
	}
	public setAnimationModes(modes: AnimationMap): void {
		this.getAnimationModes = () => modes;
	}

	public findItem(dataItemId: string): { group: string, item: MapItemMetaData } {
		if (this.mapItems && this.dataItem2mapItemMap) {
			const keys = this.dataItem2mapItemMap.get(dataItemId);
			if (keys) {
				let items = Array.from(this.mapItems.entries());
				return { group: items[keys[0]][0], item: items[keys[0]][1][keys[1]] }
			} else {
				return undefined;
			}
		}
		return undefined;
	}

	public getItem(elemId: string): { group: string, item: MapItemMetaData } {
		if (this.mapItems) {
			let item = Array.from(this.mapItems.entries())
				.reduce((accu, entry) => {
					if (!accu.item) {
						accu.group = entry[0];
						//following line should produce an array of length 0 or 1 (1 being the elemMeta with correct elemId)
						let elemMetaList = entry[1].filter(elemMeta => elemMeta.elemId === elemId);
						if (elemMetaList.length > 0) {
							accu.item = elemMetaList[0];
							if (elemMetaList.length > 1) {
								console.trace(`Multiple (${elemMetaList.length}) elements found in map-overlay for elemId ${elemId} while only one is expected.`);
							}
						}
					}
					return accu;
				}, <{ group: string, item: MapItemMetaData }>{});

			if (!item.item) {
				console.trace(`Expected to find element ${elemId} in map-overlay but it was not found.`);
			} else {
				return item;
			}
		}
		return undefined;
	}

	public highlight(container: HTMLElement): void {

		Object.entries(this.getData()).forEach(entry => {
			let group = entry[0];
			let data = entry[1];
			let dataIds = data.getMeta().selected;

			if (this.mapItems) {
				let mapItemsGroup = this.mapItems.get(group);
				if (mapItemsGroup) {
					mapItemsGroup.flat().forEach(mpItem => mpItem.selected = false);
				}
			}

			let elemIds: string[] = dataIds.reduce((elemIds, dataId) => {
				let elem = this.findItem(dataId);
				if (elem && !elemIds.includes(elem.item.elemId)) {
					elemIds.push(elem.item.elemId);
					elem.item.selected = true
				}
				return elemIds;
			}, []);

			container.querySelectorAll(`.${group}`).forEach(item => {
				if (elemIds.includes(item.id)) {
					item.classList.add('highlight');
				} else {
					item.classList.remove('highlight');
				}
			});

		});
	}

	public getBounds(): GeodeticBounds {
		return undefined;
	}

	public draw(container: HTMLElement, className: string, localProjection: PixelProjection): VisualId[] {

		let layoutResult = this.buildMapItems(this.getData(), this.getAnimationModes(), localProjection, this.getAnimationPosition());

		//identify all MapItems that do not occur in the new list anymore
		let toRemove = this.findObsoleteMapItems(layoutResult.mpItems);
		// remove
		toRemove.map(elemId => document.getElementById(elemId)).filter(elem => elem != undefined).forEach(elem => {
			if (elem.parentElement) {
				//it sometimes happens that the parentElement is null. Hence the test
				elem.parentElement.removeChild(elem);
			}
		});

		this.mapItems = layoutResult.mpItems;
		this.dataItem2mapItemMap = layoutResult.dataItem2mpItemMap;

		Array.from(this.mapItems.entries()).forEach((groupEntry, groupIndex) => {
			let mpItems = groupEntry[1];
			let itemsClassName = `${className} ${groupEntry[0]}`;
			mpItems.forEach((mpItem) => {
				let domElem = document.getElementById(mpItem.elemId);
				if (!domElem) {
					domElem = this.buildDOMElem(mpItem, itemsClassName);
					// append first, then call layout, otherwise domElem might get passed down to
					// a visualizers draw function with zero width/height if it relies on the sizes
					// within the dom element itself (rather than looking at any data item metadata)
					container.appendChild(domElem);
					this.layoutDOMElem(mpItem, localProjection, domElem);
				} else {
					this.layoutDOMElem(mpItem, localProjection, domElem);
				}
			});

		});

		return layoutResult.mpItemList;
	}


	private buildMapItems(dataMap: DataMap, modes: AnimationMap, projection: PixelProjection, animationPosition: CursorMillis): MapLayoutResult {
		let dataItem2mpItemMap: Map<string, number[]> = new Map();

		let mpItems = Array.from(Object.entries(dataMap))
			.map(groupEntry => [groupEntry[0], this.mapDataItemsToMapItems(groupEntry[1].getData(animationPosition, modes[groupEntry[0]]), projection, groupEntry[1].getMeta().selected)])
			.reduce((map, entry) => map.set(<string>entry[0], <MapItemMetaData[]>entry[1]), new Map<string, MapItemMetaData[]>());

		let mpReturnItemList: { elemId: string, dataIds: string[] }[] = [];
		Array.from(mpItems.entries()).forEach((entry, groupIndex) => {
			let group = entry[0];
			let row = entry[1];
			row.forEach((mpItem, mpItemIndex) => {
				let mpItemReference = [groupIndex, mpItemIndex];
				mpItem.dataItemIds.forEach(dataItemId => dataItem2mpItemMap.set(dataItemId, mpItemReference));
				mpReturnItemList.push({ elemId: mpItem.elemId, dataIds: [].concat(mpItem.dataItemIds) });

			});
		});

		return { mpItems: mpItems, dataItem2mpItemMap: dataItem2mpItemMap, mpItemList: mpReturnItemList };
	}

	/**
   * For each group the following steps are performed:
   * - sort dataItems on there spatial properties (roughly speaking on latitude and longitude ( when altitudes equal ))
   * - calculate for each data item the graphical dimensions and position on the map
   * - then cluster all data items  (now mapped to MapItemMetaData) that would
   *   visually overlap. This is only done when this.options.layout is set to MapLayout.combine .
   *   When this option is set to another value, each item is a cluster on its own.
   * - for each cluster construct an id that will be used as the HTMLElement id.
   *   The ids are deterministic, based on dataItem.id, dataItem.lat/north and dataItem.lng/west present
   *   in that cluster
   *   
   * @param dataItems the dataItems in one group that should be mapped to (combined) MapItems
   */
	private mapDataItemsToMapItems(dataItems: Datum[], projection: PixelProjection, selectedIds: string[]): MapItemMetaData[] {
		const size = this.renderer.size();

		let mapItems = dataItems.sort(this.datumReader.compareByLocation)
			.map(dataItem => { return { dataItem: dataItem, mpItem: this.mapDataItemToMapItemBounds(dataItem, projection, size) }; });

		let combinedMapItems = mapItems.map(mapItem => {
			let dim: { [key: string]: Datum } = {};
			dim[mapItem.dataItem.id] = mapItem.dataItem;
			return Object.assign(mapItem.mpItem, { dataItems: dim, dataItemIds: [mapItem.dataItem.id], bounds: [mapItem.mpItem], count: 1, elemId: undefined, selected: selectedIds.includes(mapItem.dataItem.id) });
		});

		if (this.options.layout === MapLayout.combine && combinedMapItems.length > 1) {
			//combine mapItems that overlap
			let clusterCount: number = undefined;
			do {

				clusterCount = combinedMapItems.length;
				let grownClusters: MapItemMetaData[] = [];

				while (combinedMapItems.length > 0) {

					let growingCluster = combinedMapItems.splice(0, 1)[0];
					grownClusters.push(growingCluster);
					let overlappingClusters: MapItemMetaData[] = [];
					let overlappingCluster: MapItemMetaData;
					for (let i = combinedMapItems.length - 1; i >= 0; i--) {

						if (this.renderer.overlap(growingCluster, combinedMapItems[i])) {
							overlappingCluster = combinedMapItems.splice(i, 1)[0];
							overlappingClusters.push(overlappingCluster);
							growingCluster.type = growingCluster.type | overlappingCluster.type | MapItemType.combined,
								growingCluster.count += overlappingCluster.count;
							growingCluster.dataItemIds = growingCluster.dataItemIds.concat(overlappingCluster.dataItemIds);
							growingCluster.bounds = growingCluster.bounds.concat(overlappingCluster.bounds);
							growingCluster.selected = growingCluster.selected || overlappingCluster.selected;
							Object.entries(overlappingCluster.dataItems).forEach(entry => growingCluster.dataItems[entry[0]] = entry[1]);
						}

					}
					if (overlappingClusters.length > 0) {
						//calculate the new cluster size
						growingCluster = <MapItemMetaData>this.renderer.merge(overlappingClusters.splice(0, 0, growingCluster), this.renderer.size());

					}

				}

				combinedMapItems = grownClusters;

			} while (clusterCount !== combinedMapItems.length);


		}

		//give the mapItems an id and calculate the geodetic properties and return
		return combinedMapItems.map(mapItem => {
			if (mapItem.northPX != undefined && mapItem.north == undefined) {
				let ne = projection.fromPixelToLatLng({ x: mapItem.eastPX, y: mapItem.northPX });
				let sw = projection.fromPixelToLatLng({ x: mapItem.westPX, y: mapItem.southPX });
				mapItem.north = ne.lat;
				mapItem.east = ne.lng;
				mapItem.south = sw.lat;
				mapItem.west = sw.lng;
			} else {
				mapItem.north = undefined;
				mapItem.east = undefined;
				mapItem.south = undefined;
				mapItem.west = undefined;
			}
			if (mapItem.latPX != undefined && mapItem.lat == undefined) {
				let latlng = projection.fromPixelToLatLng({ x: mapItem.lngPX, y: mapItem.latPX });
				mapItem.lat = latlng.lat;
				mapItem.lng = latlng.lng;
			} else {
				mapItem.lat = undefined;
				mapItem.lng = undefined;
			}

			mapItem.elemId = this.mapMapDataClusterToDOMId(mapItem);
			return mapItem;
		});
	}

	private mapDataItemToMapItemBounds(dataItem: Datum, projection: PixelProjection, size: Size): MapItemBounds {
		let type: MapItemType;

		let latlng: Geodetic, latlngpx: Pixel, wpx: number, hpx: number;
		let ne: Geodetic, sw: Geodetic, nepx: Pixel, swpx: Pixel

		if (this.datumReader.isPoint(dataItem)) {
			type = MapItemType.location;
			latlng = { lat: this.datumReader.getLat(dataItem), lng: this.datumReader.getLng(dataItem) };
			latlngpx = projection.fromLatLngToPixel(latlng);
			wpx = size.width;
			hpx = size.height;
			nepx = { x: latlngpx.x + wpx / 2, y: latlngpx.y - hpx / 2 };
			swpx = { x: nepx.x - wpx, y: nepx.y + hpx };
			ne = projection.fromPixelToLatLng(nepx);
			sw = projection.fromPixelToLatLng(swpx);
		} else {
			type = MapItemType.area;
			ne = { lat: this.datumReader.getNorth(dataItem), lng: this.datumReader.getEast(dataItem) };
			nepx = projection.fromLatLngToPixel(ne);
			sw = { lat: this.datumReader.getSouth(dataItem), lng: this.datumReader.getWest(dataItem) };
			swpx = projection.fromLatLngToPixel(sw);
			wpx = nepx.x - swpx.x;
			hpx = swpx.y - nepx.y;
			latlngpx = { x: swpx.x + wpx / 2, y: nepx.y + hpx / 2 };
			latlng = projection.fromPixelToLatLng(latlngpx);
		}

		return {
			type: type,
			north: ne.lat,
			northPX: nepx.y,
			east: ne.lng,
			eastPX: nepx.x,
			south: sw.lat,
			southPX: swpx.y,
			west: sw.lng,
			westPX: swpx.x,
			lat: latlng.lat,
			latPX: latlngpx.y,
			lng: latlng.lng,
			lngPX: latlngpx.x,
			widthPX: wpx,
			heightPX: hpx
		};
	}

	private mapMapDataClusterToDOMId(cluster: MapDataCluster): string {
		return this.mapPrefix + createElemSelector(
			[].concat(cluster.dataItemIds).sort().map(id => {
				let dataItem = cluster.dataItems[id];
				return this.datumReader.spatialHash(dataItem);
			})
		);
	}
	private findObsoleteMapItems(mapItems: Map<string, MapItemMetaData[]>): string[] {
		let toRemove: string[] = [];
		if (this.mapItems) {
			let pluckId = (item: MapItemMetaData) => item.elemId;
			Array.from(this.mapItems.entries()).forEach(groupEntry => {
				let newGroup = mapItems.get(groupEntry[0]);
				if (newGroup) {
					toRemove = toRemove.concat(groupEntry[1].flat().filter(item => newGroup.findIndex(newItem => newItem.elemId === item.elemId) === -1).map(pluckId));
				} else {
					//complete group must be removed
					toRemove = toRemove.concat(groupEntry[1].flat().map(pluckId));
				}
			});
		}

		return toRemove;
	}
	private layoutDOMElem(meta: MapItemMetaData, projection: PixelProjection, domElem?: HTMLElement): HTMLElement {
		let elem = domElem && domElem.id === meta.elemId ? domElem : <HTMLElement>document.getElementById(meta.elemId);

		elem.style.left = meta.westPX + 'px';
		elem.style.top = meta.northPX + 'px';

		elem.style.width = meta.widthPX + 'px';
		elem.style.height = meta.heightPX + 'px';

		if (meta.selected) {
			elem.classList.add('highlight');
		} else {
			elem.classList.remove('highlight');
		}

		if (this.renderer) {
			this.renderer.draw(elem, meta, this.renderer.size(), (latlng: Geodetic): Pixel => projection.fromLatLngToPixel(latlng));
		}
		return elem;
	}

	private buildDOMElem(meta: MapItemMetaData, className: string): HTMLElement {
		return <HTMLElement>markup2element(`<div id="${meta.elemId}" class="tlmd-overlay-marker ${className}" ></div>`);
	}

}
